import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getValidToken } from '../utils/authUtils';

const AdminAlumniResearchAssignments = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Main tab (research vs outreach) ---
  const [activeMainTab, setActiveMainTab] = useState(searchParams.get('tab') || 'research');

  // --- Access & identity ---
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentAdminId, setCurrentAdminId] = useState(null);
  const [currentAdminName, setCurrentAdminName] = useState('');

  // --- Config (visibility permissions) ---
  const [config, setConfig] = useState(null);
  const [peers, setPeers] = useState([]);
  const [viewingAdminId, setViewingAdminId] = useState(null);
  const [viewingAdminName, setViewingAdminName] = useState('');

  // --- Research assignments data ---
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsTotal, setAssignmentsTotal] = useState(0);
  const [assignmentsPage, setAssignmentsPage] = useState(1);
  const [assignmentsTotalPages, setAssignmentsTotalPages] = useState(1);
  const [stats, setStats] = useState(null);

  // --- Outreach assignments data ---
  const [outreachItems, setOutreachItems] = useState([]);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [outreachTotal, setOutreachTotal] = useState(0);
  const [outreachPage, setOutreachPage] = useState(1);
  const [outreachTotalPages, setOutreachTotalPages] = useState(1);
  const [outreachStats, setOutreachStats] = useState(null);

  // --- Outreach modals ---
  const [touchpointModal, setTouchpointModal] = useState(null); // assignment obj or null
  const [touchpointForm, setTouchpointForm] = useState({ channel: 'phone', direction: 'outbound', subject: '', notes: '', outcome: '' });
  const [touchpointSaving, setTouchpointSaving] = useState(false);
  const [closeModal, setCloseModal] = useState(null); // assignment obj or null
  const [closeForm, setCloseForm] = useState({ outcome: 'successful', outcome_notes: '' });
  const [closeSaving, setCloseSaving] = useState(false);
  const [stageUpdating, setStageUpdating] = useState({});
  const [touchpointList, setTouchpointList] = useState(null); // { assignmentId, items } or null
  const [touchPrompt, setTouchPrompt] = useState(null); // { assignmentId, stage } — nudge to log a touch

  // --- Outreach global stats (superadmin) ---
  const [showOutreachGlobalStats, setShowOutreachGlobalStats] = useState(false);
  const [outreachGlobalStats, setOutreachGlobalStats] = useState(null);

  // --- Filters ---
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [outreachStageFilter, setOutreachStageFilter] = useState('');
  const [outreachSearch, setOutreachSearch] = useState('');

  // --- URL editing (research) ---
  const [urlEdits, setUrlEdits] = useState({});
  const [urlSaving, setUrlSaving] = useState({});
  const [urlEditMode, setUrlEditMode] = useState({});
  const [copied, setCopied] = useState({});

  // --- Not Found modal (research) ---
  const [notFoundContactId, setNotFoundContactId] = useState(null);
  const [notFoundNote, setNotFoundNote] = useState('');

  // --- Superadmin permission management ---
  const [showPermissions, setShowPermissions] = useState(false);
  const [allConfigs, setAllConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState({});

  // --- Global assignment stats (superadmin) ---
  const [showGlobalStats, setShowGlobalStats] = useState(false);
  const [globalStats, setGlobalStats] = useState(null);

  // --- Review Queue (superadmin) ---
  const [queueItems, setQueueItems] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queuePage, setQueuePage] = useState(1);
  const [queueTotalPages, setQueueTotalPages] = useState(1);
  const [queueStats, setQueueStats] = useState(null);
  const [queueStatusFilter, setQueueStatusFilter] = useState('');
  const [queueSearch, setQueueSearch] = useState('');

  // --- Review Queue modals ---
  const [dismissModal, setDismissModal] = useState(null); // queue item or null
  const [dismissNotes, setDismissNotes] = useState('');
  const [dismissSaving, setDismissSaving] = useState(false);
  const [createOutreachModal, setCreateOutreachModal] = useState(null); // queue item or null
  const [outreachForm, setOutreachForm] = useState({ assigned_to_id: '', goal: 'board_recruitment', priority: 'normal', due_date: '', purpose: '' });
  const [outreachFormSaving, setOutreachFormSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);

  // --- Bulk selection ---
  const [selectedQueueIds, setSelectedQueueIds] = useState(new Set());
  const [bulkOutreachModal, setBulkOutreachModal] = useState(false);
  const [bulkOutreachForm, setBulkOutreachForm] = useState({ assigned_to_id: '', goal: 'board_recruitment', priority: 'normal', due_date: '', purpose: '' });
  const [bulkOutreachSaving, setBulkOutreachSaving] = useState(false);
  const [bulkDismissModal, setBulkDismissModal] = useState(false);
  const [bulkDismissNotes, setBulkDismissNotes] = useState('');
  const [bulkDismissSaving, setBulkDismissSaving] = useState(false);

  // --- Error ---
  const [error, setError] = useState(null);

  const authHeaders = () => {
    const token = getValidToken();
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Sync URL with tab
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeMainTab) {
      setActiveMainTab(tabFromUrl);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (tab) => {
    setActiveMainTab(tab);
    setSearchParams({ tab });
    setError(null);
  };

  // ============================================
  // ACCESS CHECK
  // ============================================
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = getValidToken();
        if (!token) { setAccessDenied(true); setAccessChecked(true); return; }
        const res = await axios.get(`${API_URL}/api/admin/me`, authHeaders());
        if (res.data?.hide_alumni_management) {
          setAccessDenied(true);
        }
        setIsSuperAdmin(res.data?.is_super_admin || false);
        setCurrentAdminId(res.data?.id || null);
        setCurrentAdminName(res.data?.full_name || '');
      } catch (err) {
        console.error('Access check failed:', err);
        setAccessDenied(true);
      } finally {
        setAccessChecked(true);
      }
    };
    checkAccess();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // LOAD CONFIG & PEERS
  // ============================================
  useEffect(() => {
    if (!accessChecked || accessDenied || !currentAdminId) return;
    const loadConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/exec/contacts/my-assignments/config`, authHeaders());
        setConfig(res.data);
        setPeers(res.data.peers || []);
      } catch (err) {
        console.error('Error loading config:', err);
      }
    };
    loadConfig();
  }, [accessChecked, accessDenied, currentAdminId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // FETCH RESEARCH ASSIGNMENTS
  // ============================================
  const fetchAssignments = useCallback(async (page = 1) => {
    setAssignmentsLoading(true);
    setError(null);
    try {
      let url;
      const params = new URLSearchParams({ page, page_size: 50 });
      if (statusFilter) params.set('research_status', statusFilter);
      if (searchQuery) params.set('search', searchQuery);

      if (viewingAdminId) {
        url = `${API_URL}/api/exec/contacts/assignments/${viewingAdminId}?${params}`;
      } else {
        url = `${API_URL}/api/exec/contacts/my-assignments?${params}`;
      }

      const res = await axios.get(url, authHeaders());
      setAssignments(res.data.items || []);
      setAssignmentsTotal(res.data.total || 0);
      setAssignmentsPage(res.data.page || 1);
      setAssignmentsTotalPages(res.data.total_pages || 1);
      if (res.data.stats) setStats(res.data.stats);
      if (res.data.admin_name) setViewingAdminName(res.data.admin_name);
    } catch (err) {
      console.error('Error fetching assignments:', err);
      if (err.response?.status === 403) {
        setError('You don\'t have permission to view these assignments.');
      } else {
        setError('Failed to load assignments.');
      }
    } finally {
      setAssignmentsLoading(false);
    }
  }, [viewingAdminId, statusFilter, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!accessChecked || accessDenied || !currentAdminId || activeMainTab !== 'research') return;
    fetchAssignments(1);
  }, [accessChecked, accessDenied, currentAdminId, viewingAdminId, statusFilter, fetchAssignments, activeMainTab]);

  // ============================================
  // FETCH OUTREACH ASSIGNMENTS
  // ============================================
  const fetchOutreach = useCallback(async (page = 1) => {
    setOutreachLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      if (outreachStageFilter) params.set('stage', outreachStageFilter);
      if (outreachSearch) params.set('search', outreachSearch);

      let url;
      if (viewingAdminId) {
        url = `${API_URL}/api/exec/outreach/assignments/${viewingAdminId}?${params}`;
      } else {
        url = `${API_URL}/api/exec/outreach/my-assignments?${params}`;
      }

      const res = await axios.get(url, authHeaders());
      setOutreachItems(res.data.items || []);
      setOutreachTotal(res.data.total || 0);
      setOutreachPage(res.data.page || 1);
      setOutreachTotalPages(res.data.total_pages || 1);
      if (res.data.stats) setOutreachStats(res.data.stats);
      if (res.data.admin_name) setViewingAdminName(res.data.admin_name);
    } catch (err) {
      console.error('Error fetching outreach:', err);
      if (err.response?.status === 403) {
        setError('You don\'t have permission to view these assignments.');
      } else {
        setError('Failed to load outreach assignments.');
      }
    } finally {
      setOutreachLoading(false);
    }
  }, [viewingAdminId, outreachStageFilter, outreachSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!accessChecked || accessDenied || !currentAdminId || activeMainTab !== 'outreach') return;
    fetchOutreach(1);
  }, [accessChecked, accessDenied, currentAdminId, viewingAdminId, outreachStageFilter, fetchOutreach, activeMainTab]);

  // ============================================
  // RESEARCH ACTIONS
  // ============================================
  const handleSaveUrl = async (contactId) => {
    const url = urlEdits[contactId];
    if (!url?.trim()) return;
    setUrlSaving(prev => ({ ...prev, [contactId]: true }));
    try {
      await axios.patch(`${API_URL}/api/exec/contacts/${contactId}/linkedin-url`, { linkedin_url: url }, authHeaders());
      setUrlEdits(prev => { const next = { ...prev }; delete next[contactId]; return next; });
      setUrlEditMode(prev => { const next = { ...prev }; delete next[contactId]; return next; });
      fetchAssignments(assignmentsPage);
    } catch (err) {
      setError(`Failed to save URL: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUrlSaving(prev => ({ ...prev, [contactId]: false }));
    }
  };

  const handleClearUrl = async (contactId) => {
    if (!window.confirm('Clear this LinkedIn URL and reset status to Pending?')) return;
    setUrlSaving(prev => ({ ...prev, [contactId]: true }));
    try {
      await axios.patch(`${API_URL}/api/exec/contacts/${contactId}/linkedin-url`, { linkedin_url: '' }, authHeaders());
      setUrlEdits(prev => { const next = { ...prev }; delete next[contactId]; return next; });
      setUrlEditMode(prev => { const next = { ...prev }; delete next[contactId]; return next; });
      fetchAssignments(assignmentsPage);
    } catch (err) {
      setError(`Failed to clear URL: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUrlSaving(prev => ({ ...prev, [contactId]: false }));
    }
  };

  const handleCopySearch = (contact) => {
    const text = `${contact.first_name || ''} ${contact.last_name || ''} ${contact.university || ''}`.trim();
    navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [contact.id]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [contact.id]: false })), 2000);
  };

  const handleMarkNotFound = async (contactId, note) => {
    try {
      await axios.post(`${API_URL}/api/exec/contacts/${contactId}/mark-not-found`, { note }, authHeaders());
      setNotFoundContactId(null);
      setNotFoundNote('');
      fetchAssignments(assignmentsPage);
    } catch (err) {
      setError(`Failed to mark not found: ${err.response?.data?.detail || err.message}`);
    }
  };

  // ============================================
  // OUTREACH ACTIONS
  // ============================================
  const handleStageChange = async (assignmentId, newStage) => {
    setStageUpdating(prev => ({ ...prev, [assignmentId]: true }));
    setTouchPrompt(null);
    try {
      await axios.put(`${API_URL}/api/exec/outreach/assignments/${assignmentId}/stage`, { stage: newStage }, authHeaders());
      fetchOutreach(outreachPage);
      if (newStage === 'contacted' || newStage === 'engaged') {
        setTouchPrompt({ assignmentId, stage: newStage });
      }
    } catch (err) {
      setError(`Failed to update stage: ${err.response?.data?.detail || err.message}`);
    } finally {
      setStageUpdating(prev => ({ ...prev, [assignmentId]: false }));
    }
  };

  const handleLogTouchpoint = async () => {
    if (!touchpointModal) return;
    setTouchpointSaving(true);
    try {
      await axios.post(`${API_URL}/api/exec/outreach/assignments/${touchpointModal.id}/touchpoints`, touchpointForm, authHeaders());
      setTouchpointModal(null);
      setTouchpointForm({ channel: 'phone', direction: 'outbound', subject: '', notes: '', outcome: '' });
      setTouchPrompt(null);
      fetchOutreach(outreachPage);
    } catch (err) {
      setError(`Failed to log touchpoint: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTouchpointSaving(false);
    }
  };

  const handleCloseAssignment = async () => {
    if (!closeModal) return;
    setCloseSaving(true);
    try {
      await axios.put(`${API_URL}/api/exec/outreach/assignments/${closeModal.id}/close`, closeForm, authHeaders());
      setCloseModal(null);
      setCloseForm({ outcome: 'successful', outcome_notes: '' });
      fetchOutreach(outreachPage);
    } catch (err) {
      setError(`Failed to close assignment: ${err.response?.data?.detail || err.message}`);
    } finally {
      setCloseSaving(false);
    }
  };

  const handleViewTouchpoints = async (assignment) => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/outreach/assignments/${assignment.id}/touchpoints`, authHeaders());
      setTouchpointList({ assignmentId: assignment.id, alumniName: assignment.alumni_name, items: res.data.items || [] });
    } catch (err) {
      setError(`Failed to load touchpoints: ${err.response?.data?.detail || err.message}`);
    }
  };

  // ============================================
  // SUPERADMIN: PERMISSIONS
  // ============================================
  const fetchAllConfigs = async () => {
    setConfigsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/contacts/assignment-config/all`, authHeaders());
      setAllConfigs(res.data);
    } catch (err) {
      console.error('Error fetching configs:', err);
    } finally {
      setConfigsLoading(false);
    }
  };

  const handleTogglePermission = async (adminId, field, currentValue) => {
    setConfigSaving(prev => ({ ...prev, [`${adminId}-${field}`]: true }));
    try {
      await axios.put(`${API_URL}/api/exec/contacts/assignment-config/${adminId}`, {
        [field]: !currentValue,
      }, authHeaders());
      setAllConfigs(prev => prev.map(c =>
        c.admin_id === adminId ? { ...c, [field]: !currentValue } : c
      ));
    } catch (err) {
      setError(`Failed to update permission: ${err.response?.data?.detail || err.message}`);
    } finally {
      setConfigSaving(prev => ({ ...prev, [`${adminId}-${field}`]: false }));
    }
  };

  // ============================================
  // SUPERADMIN: GLOBAL STATS
  // ============================================
  const fetchGlobalStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/contacts/assignment-stats`, authHeaders());
      setGlobalStats(res.data);
    } catch (err) {
      console.error('Error fetching global stats:', err);
    }
  };

  const fetchOutreachGlobalStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/outreach/assignment-stats`, authHeaders());
      setOutreachGlobalStats(res.data);
    } catch (err) {
      console.error('Error fetching outreach global stats:', err);
    }
  };

  // ============================================
  // REVIEW QUEUE (superadmin)
  // ============================================
  const fetchQueue = useCallback(async (page = 1) => {
    setQueueLoading(true);
    setError(null);
    setSelectedQueueIds(new Set());
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      if (queueStatusFilter) params.set('status', queueStatusFilter);
      if (queueSearch) params.set('search', queueSearch);

      const res = await axios.get(`${API_URL}/api/exec/leadership-review-queue?${params}`, authHeaders());
      setQueueItems(res.data.items || []);
      setQueueTotal(res.data.total || 0);
      setQueuePage(res.data.page || 1);
      setQueueTotalPages(res.data.total_pages || 1);
      if (res.data.stats) setQueueStats(res.data.stats);
    } catch (err) {
      console.error('Error fetching review queue:', err);
      if (err.response?.status === 403) {
        setError('Only superadmins can view the review queue.');
      } else {
        setError('Failed to load review queue.');
      }
    } finally {
      setQueueLoading(false);
    }
  }, [queueStatusFilter, queueSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!accessChecked || accessDenied || !currentAdminId || !isSuperAdmin || activeMainTab !== 'review-queue') return;
    fetchQueue(1);
  }, [accessChecked, accessDenied, currentAdminId, isSuperAdmin, activeMainTab, queueStatusFilter, fetchQueue]);

  // Fetch queue stats for tab badge on initial load (superadmin only)
  useEffect(() => {
    if (!accessChecked || accessDenied || !isSuperAdmin) return;
    const fetchQueueStats = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/exec/leadership-review-queue/stats`, authHeaders());
        setQueueStats(res.data);
      } catch (err) {
        // Silent fail for badge - not critical
      }
    };
    fetchQueueStats();
  }, [accessChecked, accessDenied, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStaffList = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/contacts/assignment-config/all`, authHeaders());
      setStaffList(res.data.map(c => ({ id: c.admin_id, name: c.full_name })));
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  };

  const handleDismissQueueItem = async () => {
    if (!dismissModal) return;
    setDismissSaving(true);
    try {
      await axios.put(`${API_URL}/api/exec/leadership-review-queue/${dismissModal.id}/dismiss`, { notes: dismissNotes }, authHeaders());
      setDismissModal(null);
      setDismissNotes('');
      fetchQueue(queuePage);
    } catch (err) {
      setError(`Failed to dismiss: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDismissSaving(false);
    }
  };

  const handleCreateOutreachFromQueue = async () => {
    if (!createOutreachModal) return;
    setOutreachFormSaving(true);
    try {
      await axios.post(`${API_URL}/api/exec/leadership-review-queue/${createOutreachModal.id}/create-outreach`, outreachForm, authHeaders());
      setCreateOutreachModal(null);
      setOutreachForm({ assigned_to_id: '', goal: 'board_recruitment', priority: 'normal', due_date: '', purpose: '' });
      fetchQueue(queuePage);
    } catch (err) {
      setError(`Failed to create outreach: ${err.response?.data?.detail || err.message}`);
    } finally {
      setOutreachFormSaving(false);
    }
  };

  // --- Bulk selection helpers ---
  const pendingQueueItems = queueItems.filter(i => i.status === 'pending');
  const selectedPendingIds = new Set([...selectedQueueIds].filter(id => pendingQueueItems.some(i => i.id === id)));
  const allPendingSelected = pendingQueueItems.length > 0 && pendingQueueItems.every(i => selectedQueueIds.has(i.id));

  const toggleSelectQueueItem = (id) => {
    setSelectedQueueIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllPending = () => {
    if (allPendingSelected) {
      setSelectedQueueIds(new Set());
    } else {
      setSelectedQueueIds(new Set(pendingQueueItems.map(i => i.id)));
    }
  };

  const handleBulkDismiss = async () => {
    if (selectedPendingIds.size === 0) return;
    setBulkDismissSaving(true);
    try {
      await axios.post(`${API_URL}/api/exec/leadership-review-queue/bulk-dismiss`, {
        item_ids: [...selectedPendingIds],
        notes: bulkDismissNotes.trim() || null,
      }, authHeaders());
      setBulkDismissModal(false);
      setBulkDismissNotes('');
      setSelectedQueueIds(new Set());
      fetchQueue(queuePage);
    } catch (err) {
      setError(`Bulk dismiss failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBulkDismissSaving(false);
    }
  };

  const handleBulkCreateOutreach = async () => {
    if (selectedPendingIds.size === 0 || !bulkOutreachForm.assigned_to_id) return;
    setBulkOutreachSaving(true);
    try {
      await axios.post(`${API_URL}/api/exec/leadership-review-queue/bulk-create-outreach`, {
        item_ids: [...selectedPendingIds],
        ...bulkOutreachForm,
      }, authHeaders());
      setBulkOutreachModal(false);
      setBulkOutreachForm({ assigned_to_id: '', goal: 'board_recruitment', priority: 'normal', due_date: '', purpose: '' });
      setSelectedQueueIds(new Set());
      fetchQueue(queuePage);
    } catch (err) {
      setError(`Bulk outreach failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBulkOutreachSaving(false);
    }
  };

  const scoreBadge = (score) => {
    const s = parseFloat(score);
    if (s >= 8) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{s.toFixed(1)}</span>;
    if (s >= 7) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700">{s.toFixed(1)}</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700">{s.toFixed(1)}</span>;
  };

  // ============================================
  // RENDER HELPERS
  // ============================================
  const stageBadge = (stage) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      engaged: 'bg-purple-100 text-purple-700',
      active: 'bg-green-100 text-green-700',
      dormant: 'bg-gray-100 text-gray-600',
      closed: 'bg-gray-200 text-gray-500',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[stage] || 'bg-gray-100 text-gray-600'}`}>
        {stage ? stage.charAt(0).toUpperCase() + stage.slice(1) : 'Unknown'}
      </span>
    );
  };

  const priorityBadge = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      normal: 'bg-gray-100 text-gray-600',
      low: 'bg-gray-50 text-gray-400',
    };
    if (priority === 'normal') return null;
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[priority] || ''}`}>
        {priority ? priority.toUpperCase() : ''}
      </span>
    );
  };

  const goalLabel = (goal) => {
    const labels = {
      membership_upgrade: 'Membership Upgrade',
      donation: 'Donation',
      event_attendance: 'Event Attendance',
      engagement: 'Engagement',
      board_recruitment: 'Board Recruitment',
      other: 'Other',
    };
    return labels[goal] || goal || '-';
  };

  // ============================================
  // RENDER
  // ============================================
  if (!accessChecked) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>;
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <div className="text-4xl mb-4">&#128274;</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view this page.</p>
          <button onClick={() => navigate('/admin')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  const isViewingOwn = !viewingAdminId || viewingAdminId === currentAdminId;
  const canViewPeers = config?.can_view_peers || false;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-400 hover:text-gray-600"
                title="Back to Admin"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {isViewingOwn ? 'My Assignments' : `${viewingAdminName}'s Assignments`}
                </h1>
                <p className="text-sm text-gray-500">
                  {activeMainTab === 'research' ? 'Alumni LinkedIn Research' : activeMainTab === 'review-queue' ? 'Leadership Score Review Queue' : 'Alumni Outreach Pipeline'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Peer switcher */}
              {(canViewPeers || isSuperAdmin) && peers.length > 0 && (
                <select
                  value={viewingAdminId || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setViewingAdminId(val ? parseInt(val) : null);
                    setAssignmentsPage(1);
                    setOutreachPage(1);
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">My Assignments</option>
                  {peers.map(p => (
                    <option key={p.id} value={p.id}>{p.full_name}</option>
                  ))}
                </select>
              )}

              {/* Superadmin buttons */}
              {isSuperAdmin && (
                <>
                  <button
                    onClick={() => {
                      if (activeMainTab === 'research') {
                        setShowGlobalStats(!showGlobalStats);
                        if (!showGlobalStats) fetchGlobalStats();
                      } else {
                        setShowOutreachGlobalStats(!showOutreachGlobalStats);
                        if (!showOutreachGlobalStats) fetchOutreachGlobalStats();
                      }
                    }}
                    className="px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
                  >
                    Team Stats
                  </button>
                  <button
                    onClick={() => { setShowPermissions(!showPermissions); if (!showPermissions) fetchAllConfigs(); }}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Permissions
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-1 mt-4 border-b border-gray-200 -mb-4">
            {[
              { key: 'research', label: 'Research', color: 'indigo' },
              { key: 'outreach', label: 'Outreach', color: 'violet' },
              ...(isSuperAdmin ? [{ key: 'review-queue', label: 'Review Queue', color: 'purple' }] : []),
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => switchTab(tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeMainTab === tab.key
                    ? `border-${tab.color}-600 text-${tab.color}-700`
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.key === 'outreach' && outreachStats?.total > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-violet-100 text-violet-700">{outreachStats.total}</span>
                )}
                {tab.key === 'review-queue' && queueStats?.pending > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-700">{queueStats.pending}</span>
                )}
              </button>
            ))}
            <button
              onClick={() => navigate('/admin/executive-intelligence?tab=brokerage')}
              className="px-4 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
            >
              Mentorship &amp; Networking Requests
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 text-sm">Dismiss</button>
          </div>
        )}

        {/* ============================================
            RESEARCH TAB
            ============================================ */}
        {activeMainTab === 'research' && (
          <>
            {/* Superadmin: Global Stats */}
            {isSuperAdmin && showGlobalStats && globalStats && (
              <div className="bg-white border border-indigo-200 rounded-lg p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-indigo-800 mb-3">Team Research Progress</h3>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-indigo-700">{globalStats.total_assigned}</div>
                    <div className="text-xs text-indigo-600">Total Assigned</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-green-700">{globalStats.total_found}</div>
                    <div className="text-xs text-green-600">Found</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-red-700">{globalStats.total_not_found}</div>
                    <div className="text-xs text-red-600">Not Found</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-yellow-700">{globalStats.total_pending}</div>
                    <div className="text-xs text-yellow-600">Pending</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-gray-700">{globalStats.unassigned}</div>
                    <div className="text-xs text-gray-600">Unassigned</div>
                  </div>
                </div>
                {globalStats.by_assignee?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-indigo-50 text-indigo-800">
                          <th className="px-3 py-2 text-left font-medium">Team Member</th>
                          <th className="px-3 py-2 text-center font-medium">Assigned</th>
                          <th className="px-3 py-2 text-center font-medium">Found</th>
                          <th className="px-3 py-2 text-center font-medium">Not Found</th>
                          <th className="px-3 py-2 text-center font-medium">Pending</th>
                          <th className="px-3 py-2 text-center font-medium">Overdue</th>
                          <th className="px-3 py-2 text-center font-medium">Completion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-indigo-100">
                        {globalStats.by_assignee.map(s => (
                          <tr
                            key={s.admin_id}
                            className="hover:bg-indigo-50/50 cursor-pointer"
                            onClick={() => { setViewingAdminId(s.admin_id); setShowGlobalStats(false); }}
                            title={`Click to view ${s.admin_name}'s assignments`}
                          >
                            <td className="px-3 py-2 font-medium text-gray-800">{s.admin_name}</td>
                            <td className="px-3 py-2 text-center">{s.assigned}</td>
                            <td className="px-3 py-2 text-center text-green-700 font-medium">{s.found}</td>
                            <td className="px-3 py-2 text-center text-red-700">{s.not_found}</td>
                            <td className="px-3 py-2 text-center text-yellow-700">{s.pending}</td>
                            <td className="px-3 py-2 text-center">{s.overdue > 0 ? <span className="text-red-600 font-bold">{s.overdue}</span> : '0'}</td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center gap-1">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                  <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${s.completion_pct}%` }} />
                                </div>
                                <span className="text-xs font-medium w-10 text-right">{s.completion_pct}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Superadmin: Permissions Panel */}
            {isSuperAdmin && showPermissions && (
              <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Assignment Visibility Permissions</h3>
                <p className="text-xs text-gray-500 mb-4">Control what each team member can see on their Assignments page.</p>
                {configsLoading ? (
                  <div className="text-center py-4 text-sm text-gray-500">Loading...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Team Member</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-700">Assigned</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-700">Can View Peers</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-700">Can View Full Queue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {allConfigs.map(c => (
                          <tr key={c.admin_id} className="hover:bg-gray-50/50">
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">{c.full_name}</div>
                              <div className="text-gray-500">{c.email}</div>
                            </td>
                            <td className="px-3 py-2 text-center font-medium">{c.assigned_count}</td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => handleTogglePermission(c.admin_id, 'can_view_peers', c.can_view_peers)}
                                disabled={configSaving[`${c.admin_id}-can_view_peers`]}
                                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                                  c.can_view_peers
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                              >
                                {configSaving[`${c.admin_id}-can_view_peers`] ? '...' : c.can_view_peers ? 'Enabled' : 'Disabled'}
                              </button>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => handleTogglePermission(c.admin_id, 'can_view_full_queue', c.can_view_full_queue)}
                                disabled={configSaving[`${c.admin_id}-can_view_full_queue`]}
                                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                                  c.can_view_full_queue
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                              >
                                {configSaving[`${c.admin_id}-can_view_full_queue`] ? '...' : c.can_view_full_queue ? 'Enabled' : 'Disabled'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Stats bar + progress (own assignments) */}
            {isViewingOwn && stats && (() => {
              const completed = (stats.found || 0) + (stats.not_found || 0);
              const total = stats.total_assigned || 0;
              const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="bg-white rounded-lg border border-gray-200 p-3 text-center shadow-sm">
                      <div className="text-lg font-bold text-gray-800">{stats.total_assigned}</div>
                      <div className="text-xs text-gray-500">Total Assigned</div>
                    </div>
                    <div className="bg-white rounded-lg border border-yellow-200 p-3 text-center shadow-sm">
                      <div className="text-lg font-bold text-yellow-600">{stats.pending}</div>
                      <div className="text-xs text-yellow-600">Pending</div>
                    </div>
                    <div className="bg-white rounded-lg border border-green-200 p-3 text-center shadow-sm">
                      <div className="text-lg font-bold text-green-600">{stats.found}</div>
                      <div className="text-xs text-green-600">Found</div>
                    </div>
                    <div className="bg-white rounded-lg border border-red-200 p-3 text-center shadow-sm">
                      <div className="text-lg font-bold text-red-600">{stats.not_found}</div>
                      <div className="text-xs text-red-600">Not Found</div>
                    </div>
                    <div className="bg-white rounded-lg border border-amber-200 p-3 text-center shadow-sm">
                      <div className="text-lg font-bold text-amber-600">{stats.overdue}</div>
                      <div className="text-xs text-amber-600">Overdue</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-indigo-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-indigo-800">Research Progress</span>
                      <span className="text-sm font-bold text-indigo-700">{pct}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${pct === 100 ? 'bg-green-500' : 'bg-indigo-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1.5 text-xs text-gray-500">
                      <span>{completed} of {total} completed</span>
                      <span>{stats.pending || 0} remaining</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-2">
                {['', 'pending', 'found', 'not_found', 'overdue'].map(f => (
                  <button
                    key={f}
                    onClick={() => { setStatusFilter(f); setAssignmentsPage(1); }}
                    className={`px-3 py-1.5 text-xs rounded-full border font-medium ${
                      statusFilter === f
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {f === '' ? 'All' : f === 'not_found' ? 'Not Found' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                <div className="flex-1" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setAssignmentsPage(1); fetchAssignments(1); } }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-56 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  onClick={() => { setAssignmentsPage(1); fetchAssignments(1); }}
                  className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Assignments table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {assignmentsLoading ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mb-3" />
                  <div className="text-sm">Loading assignments...</div>
                </div>
              ) : assignments.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-gray-700">
                          <th className="px-3 py-3 text-left font-medium sticky left-0 z-20 bg-amber-100" style={{ minWidth: '160px' }}>Name</th>
                          <th className="px-3 py-3 text-left font-medium sticky z-20 bg-amber-100" style={{ left: '160px', minWidth: '130px' }}>University</th>
                          <th className="px-3 py-3 text-left font-medium sticky z-20 bg-amber-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: '290px', minWidth: '190px' }}>Demographics</th>
                          <th className="px-3 py-3 text-left font-medium bg-purple-100 text-purple-800">Company</th>
                          <th className="px-3 py-3 text-left font-medium bg-purple-100 text-purple-800" style={{ minWidth: '280px' }}>LinkedIn URL</th>
                          <th className="px-3 py-3 text-left font-medium bg-purple-100 text-purple-800">Due</th>
                          <th className="px-3 py-3 text-left font-medium bg-purple-100 text-purple-800">Status</th>
                          <th className="px-3 py-3 text-left font-medium bg-purple-100 text-purple-800">Date Found</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {assignments.map(contact => (
                          <tr key={contact.id} className={`hover:bg-gray-50 ${contact.is_overdue ? 'bg-amber-50/50' : ''}`}>
                            <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap sticky left-0 z-10 bg-amber-50" style={{ minWidth: '160px' }}>
                              <div>
                                {contact.full_name}
                                {contact.graduation_year && <span className="text-xs text-gray-400 ml-1">'{String(contact.graduation_year).slice(-2)}</span>}
                              </div>
                              {(contact.email || contact.personal_email) && (
                                <div className="text-[11px] text-gray-400 truncate max-w-[150px]" title={contact.email || contact.personal_email}>
                                  {contact.email || contact.personal_email}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-600 sticky z-10 bg-amber-50" style={{ left: '160px', minWidth: '130px' }}>{contact.university || '-'}</td>
                            <td className="px-3 py-2 sticky z-10 bg-amber-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: '290px', minWidth: '190px' }}>
                              {(contact.major || contact.gender || contact.ethnicity) ? (
                                <div className="relative group">
                                  <div className="text-[11px] text-gray-500 cursor-default leading-tight">
                                    <div>{[contact.gender, contact.ethnicity].filter(Boolean).join(' / ') || ''}</div>
                                    {contact.major && <div className="text-gray-400 truncate max-w-[170px]">{contact.major}</div>}
                                  </div>
                                  <div className="absolute left-0 top-full mt-1 z-30 hidden group-hover:block bg-white border border-gray-200 rounded-lg shadow-lg p-2.5 min-w-[180px]">
                                    {contact.gender && <div className="text-xs text-gray-700"><span className="font-medium text-gray-500">Gender:</span> {contact.gender}</div>}
                                    {contact.ethnicity && <div className="text-xs text-gray-700 mt-0.5"><span className="font-medium text-gray-500">Ethnicity:</span> {contact.ethnicity}</div>}
                                    {contact.major && <div className="text-xs text-gray-700 mt-0.5"><span className="font-medium text-gray-500">Major:</span> {contact.major}</div>}
                                  </div>
                                </div>
                              ) : <span className="text-gray-400 text-[11px]">-</span>}
                            </td>
                            <td className="px-3 py-2 text-gray-600 bg-purple-50">{contact.current_company || '-'}</td>
                            <td className="px-3 py-2 bg-purple-50">
                              {contact.has_linkedin_url && !urlEditMode[contact.id] ? (
                                <div className="flex items-center gap-2">
                                  <a
                                    href={contact.linkedin_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline truncate max-w-[180px]"
                                    title={contact.linkedin_url}
                                  >
                                    {contact.linkedin_url.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//i, '').replace(/\/$/, '')}
                                  </a>
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                    Found
                                  </span>
                                  {isSuperAdmin && (
                                    <button
                                      onClick={() => { setUrlEditMode(prev => ({ ...prev, [contact.id]: true })); setUrlEdits(prev => ({ ...prev, [contact.id]: contact.linkedin_url })); }}
                                      className="text-gray-400 hover:text-indigo-600 text-xs"
                                      title="Edit LinkedIn URL"
                                    >
                                      Edit
                                    </button>
                                  )}
                                </div>
                              ) : contact.has_linkedin_url && urlEditMode[contact.id] ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={urlEdits[contact.id] || ''}
                                    onChange={(e) => setUrlEdits(prev => ({ ...prev, [contact.id]: e.target.value }))}
                                    placeholder="Paste LinkedIn URL..."
                                    className="border border-gray-300 rounded-lg px-2 py-1 w-44 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    onKeyDown={(e) => e.key === 'Enter' && handleSaveUrl(contact.id)}
                                  />
                                  <button
                                    onClick={() => handleSaveUrl(contact.id)}
                                    disabled={urlSaving[contact.id] || !urlEdits[contact.id]?.trim()}
                                    className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                  >
                                    {urlSaving[contact.id] ? '...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => handleClearUrl(contact.id)}
                                    disabled={urlSaving[contact.id]}
                                    className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:bg-gray-100"
                                  >
                                    Clear
                                  </button>
                                  <button
                                    onClick={() => { setUrlEditMode(prev => { const next = { ...prev }; delete next[contact.id]; return next; }); }}
                                    className="px-1.5 py-1 text-xs text-gray-400 hover:text-gray-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex flex-col gap-1.5">
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      value={urlEdits[contact.id] || ''}
                                      onChange={(e) => setUrlEdits(prev => ({ ...prev, [contact.id]: e.target.value }))}
                                      placeholder="Paste LinkedIn URL..."
                                      className="border border-gray-300 rounded-lg px-2 py-1 w-48 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                      onKeyDown={(e) => e.key === 'Enter' && handleSaveUrl(contact.id)}
                                    />
                                    <button
                                      onClick={() => handleSaveUrl(contact.id)}
                                      disabled={urlSaving[contact.id] || !urlEdits[contact.id]?.trim()}
                                      className="px-2.5 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    >
                                      {urlSaving[contact.id] ? '...' : 'Save'}
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleCopySearch(contact)}
                                      className={`px-2 py-1 text-xs rounded ${
                                        copied[contact.id]
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-purple-700 text-white hover:bg-purple-800'
                                      }`}
                                      title="Copy name + university for LinkedIn search"
                                    >
                                      {copied[contact.id] ? 'Copied!' : 'Copy'}
                                    </button>
                                    {contact.research_status !== 'not_found' && (
                                      <button
                                        onClick={() => { setNotFoundContactId(contact.id); setNotFoundNote(''); }}
                                        className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                                        title="Mark as unable to find on LinkedIn"
                                      >
                                        Not Found
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap bg-purple-50">
                              {contact.assignment_due_date ? (
                                <span className={`text-xs font-medium ${contact.is_overdue ? 'text-red-600' : 'text-gray-600'}`}>
                                  {new Date(contact.assignment_due_date + 'T00:00:00').toLocaleDateString()}
                                  {contact.is_overdue && <span className="ml-1 text-red-500 font-bold">!</span>}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2 bg-purple-50">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                ['found', 'processing', 'completed'].includes(contact.research_status) ? 'bg-green-100 text-green-700' :
                                contact.research_status === 'not_found' ? 'bg-red-100 text-red-700' :
                                contact.is_overdue ? 'bg-amber-100 text-amber-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {['found', 'processing', 'completed'].includes(contact.research_status) ? 'Found' :
                                 contact.research_status === 'not_found' ? 'Not Found' :
                                 contact.is_overdue ? 'Overdue' : 'Pending'}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap bg-purple-50 text-xs text-gray-600">
                              {contact.found_at
                                ? new Date(contact.found_at).toLocaleDateString()
                                : <span className="text-gray-400">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {assignmentsTotalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        Page {assignmentsPage} of {assignmentsTotalPages} ({assignmentsTotal.toLocaleString()} contacts)
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => fetchAssignments(assignmentsPage - 1)}
                          disabled={assignmentsPage <= 1}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => fetchAssignments(assignmentsPage + 1)}
                          disabled={assignmentsPage >= assignmentsTotalPages}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {statusFilter ? 'No contacts match this filter' : 'No assignments yet'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {statusFilter
                      ? 'Try changing the filter or clearing it.'
                      : 'Contacts will appear here when a superadmin assigns them to you.'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ============================================
            OUTREACH TAB
            ============================================ */}
        {activeMainTab === 'outreach' && (
          <>
            {/* Superadmin: Outreach Global Stats */}
            {isSuperAdmin && showOutreachGlobalStats && outreachGlobalStats && (
              <div className="bg-white border border-violet-200 rounded-lg p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-violet-800 mb-3">Team Outreach Progress</h3>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4">
                  {['total', 'new', 'contacted', 'engaged', 'active', 'dormant'].map(key => (
                    <div key={key} className="bg-violet-50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-violet-700">{outreachGlobalStats.totals?.[key] || 0}</div>
                      <div className="text-xs text-violet-600 capitalize">{key}</div>
                    </div>
                  ))}
                </div>
                {outreachGlobalStats.by_assignee?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-violet-50 text-violet-800">
                          <th className="px-3 py-2 text-left font-medium">Team Member</th>
                          <th className="px-3 py-2 text-center font-medium">Total</th>
                          <th className="px-3 py-2 text-center font-medium">New</th>
                          <th className="px-3 py-2 text-center font-medium">Contacted</th>
                          <th className="px-3 py-2 text-center font-medium">Engaged</th>
                          <th className="px-3 py-2 text-center font-medium">Active</th>
                          <th className="px-3 py-2 text-center font-medium">Overdue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-violet-100">
                        {outreachGlobalStats.by_assignee.map(s => (
                          <tr
                            key={s.admin_id}
                            className="hover:bg-violet-50/50 cursor-pointer"
                            onClick={() => { setViewingAdminId(s.admin_id); setShowOutreachGlobalStats(false); }}
                            title={`Click to view ${s.admin_name}'s outreach`}
                          >
                            <td className="px-3 py-2 font-medium text-gray-800">{s.admin_name}</td>
                            <td className="px-3 py-2 text-center font-medium">{s.total}</td>
                            <td className="px-3 py-2 text-center text-blue-700">{s.new}</td>
                            <td className="px-3 py-2 text-center text-yellow-700">{s.contacted}</td>
                            <td className="px-3 py-2 text-center text-purple-700">{s.engaged}</td>
                            <td className="px-3 py-2 text-center text-green-700">{s.active}</td>
                            <td className="px-3 py-2 text-center">{s.overdue > 0 ? <span className="text-red-600 font-bold">{s.overdue}</span> : '0'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Outreach stats bar */}
            {outreachStats && (
              <div className="grid grid-cols-2 sm:grid-cols-7 gap-3">
                {[
                  { key: 'total', label: 'Total', border: 'border-violet-200', text: 'text-violet-700' },
                  { key: 'new', label: 'New', border: 'border-blue-200', text: 'text-blue-600' },
                  { key: 'contacted', label: 'Contacted', border: 'border-yellow-200', text: 'text-yellow-600' },
                  { key: 'engaged', label: 'Engaged', border: 'border-purple-200', text: 'text-purple-600' },
                  { key: 'active', label: 'Active', border: 'border-green-200', text: 'text-green-600' },
                  { key: 'dormant', label: 'Dormant', border: 'border-gray-200', text: 'text-gray-600' },
                  { key: 'overdue', label: 'Overdue', border: 'border-red-200', text: 'text-red-600' },
                ].map(s => (
                  <div key={s.key} className={`bg-white rounded-lg border ${s.border} p-3 text-center shadow-sm`}>
                    <div className={`text-lg font-bold ${s.text}`}>{outreachStats[s.key] || 0}</div>
                    <div className={`text-xs ${s.text}`}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Outreach Progress Bars */}
            {outreachStats && outreachStats.total > 0 && (() => {
              const total = outreachStats.total;
              const contactedCount = outreachStats.contacted_with_touch || 0;
              const engagedCount = outreachStats.engaged_with_touch || 0;
              const contactedPct = Math.round((contactedCount / total) * 100);
              const engagedPct = Math.round((engagedCount / total) * 100);
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg border border-yellow-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-yellow-800">Contacted Progress</span>
                      <span className="text-sm font-bold text-yellow-700">{contactedPct}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${contactedPct === 100 ? 'bg-green-500' : 'bg-yellow-500'}`}
                        style={{ width: `${contactedPct}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1.5 text-xs text-gray-500">
                      <span>{contactedCount} of {total} contacted</span>
                      <span>{total - contactedCount} remaining</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-purple-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-purple-800">Engaged Progress</span>
                      <span className="text-sm font-bold text-purple-700">{engagedPct}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${engagedPct === 100 ? 'bg-green-500' : 'bg-purple-500'}`}
                        style={{ width: `${engagedPct}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1.5 text-xs text-gray-500">
                      <span>{engagedCount} of {total} engaged</span>
                      <span>{total - engagedCount} remaining</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Outreach filters */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-2">
                {['', 'new', 'contacted', 'engaged', 'active', 'dormant', 'overdue'].map(f => (
                  <button
                    key={f}
                    onClick={() => { setOutreachStageFilter(f); setOutreachPage(1); }}
                    className={`px-3 py-1.5 text-xs rounded-full border font-medium ${
                      outreachStageFilter === f
                        ? 'bg-violet-600 text-white border-violet-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                <div className="flex-1" />
                <input
                  type="text"
                  placeholder="Search by name, email, company..."
                  value={outreachSearch}
                  onChange={(e) => setOutreachSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setOutreachPage(1); fetchOutreach(1); } }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-56 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
                <button
                  onClick={() => { setOutreachPage(1); fetchOutreach(1); }}
                  className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Outreach table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {outreachLoading ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-violet-600 border-t-transparent mb-3" />
                  <div className="text-sm">Loading outreach assignments...</div>
                </div>
              ) : outreachItems.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-violet-50 text-violet-800">
                          <th className="px-3 py-3 text-left font-medium" style={{ minWidth: '180px' }}>Alumni</th>
                          <th className="px-3 py-3 text-left font-medium">Company</th>
                          <th className="px-3 py-3 text-left font-medium">Stage</th>
                          <th className="px-3 py-3 text-left font-medium">Goal</th>
                          <th className="px-3 py-3 text-left font-medium">Priority</th>
                          <th className="px-3 py-3 text-left font-medium">Due</th>
                          <th className="px-3 py-3 text-center font-medium">Touches</th>
                          <th className="px-3 py-3 text-left font-medium">Last Touch</th>
                          <th className="px-3 py-3 text-left font-medium" style={{ minWidth: '220px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {outreachItems.map(item => (
                          <React.Fragment key={item.id}>
                          <tr className={`hover:bg-gray-50 ${item.is_overdue ? 'bg-red-50/30' : ''}`}>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900">{item.alumni_name}</div>
                              {item.alumni_email && (
                                <div className="text-[11px] text-gray-400 truncate max-w-[170px]">{item.alumni_email}</div>
                              )}
                              {item.alumni_position && (
                                <div className="text-[11px] text-gray-500">{item.alumni_position}</div>
                              )}
                              {item.linkedin_url && (
                                <a
                                  href={item.linkedin_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 mt-0.5"
                                >
                                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                                  LinkedIn
                                </a>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-600 text-sm">{item.alumni_company || '-'}</td>
                            <td className="px-3 py-2">
                              {isViewingOwn ? (
                                <select
                                  value={item.stage}
                                  onChange={(e) => handleStageChange(item.id, e.target.value)}
                                  disabled={stageUpdating[item.id]}
                                  className="text-xs border border-gray-300 rounded px-1.5 py-1 focus:ring-2 focus:ring-violet-500"
                                >
                                  {['new', 'contacted', 'engaged', 'active', 'dormant'].map(s => (
                                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                  ))}
                                </select>
                              ) : (
                                stageBadge(item.stage)
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600">{goalLabel(item.goal)}</td>
                            <td className="px-3 py-2">
                              {priorityBadge(item.priority) || <span className="text-xs text-gray-400">Normal</span>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {item.due_date ? (
                                <span className={`text-xs font-medium ${item.is_overdue ? 'text-red-600' : 'text-gray-600'}`}>
                                  {new Date(item.due_date + 'T00:00:00').toLocaleDateString()}
                                  {item.is_overdue && <span className="ml-1 text-red-500 font-bold">!</span>}
                                </span>
                              ) : <span className="text-xs text-gray-400">-</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => handleViewTouchpoints(item)}
                                className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                                title="View touchpoints"
                              >
                                {item.touchpoint_count || 0}
                              </button>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                              {item.last_touchpoint_at
                                ? new Date(item.last_touchpoint_at).toLocaleDateString()
                                : <span className="text-gray-400">None</span>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                {isViewingOwn && (
                                  <>
                                    <button
                                      onClick={() => {
                                        setTouchpointModal(item);
                                        setTouchpointForm({ channel: 'phone', direction: 'outbound', subject: '', notes: '', outcome: '' });
                                      }}
                                      className="px-2 py-1 text-xs bg-violet-100 text-violet-700 rounded hover:bg-violet-200"
                                    >
                                      Log Touch
                                    </button>
                                    <button
                                      onClick={() => {
                                        setCloseModal(item);
                                        setCloseForm({ outcome: 'successful', outcome_notes: '' });
                                      }}
                                      className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                                    >
                                      Close
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {touchPrompt && touchPrompt.assignmentId === item.id && (
                            <tr className="bg-green-50">
                              <td colSpan={9} className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="text-sm text-green-800">
                                    What was the nature of your outreach? Record it by{' '}
                                    <button
                                      onClick={() => {
                                        setTouchPrompt(null);
                                        setTouchpointModal(item);
                                        setTouchpointForm({ channel: 'phone', direction: 'outbound', subject: '', notes: '', outcome: '' });
                                      }}
                                      className="font-semibold text-green-900 underline hover:text-green-700"
                                    >
                                      logging a touch
                                    </button>
                                    {' '}to complete the task.
                                  </span>
                                  <button onClick={() => setTouchPrompt(null)} className="ml-auto text-green-400 hover:text-green-600 text-lg">&times;</button>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {outreachTotalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        Page {outreachPage} of {outreachTotalPages} ({outreachTotal.toLocaleString()} assignments)
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => fetchOutreach(outreachPage - 1)}
                          disabled={outreachPage <= 1}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => fetchOutreach(outreachPage + 1)}
                          disabled={outreachPage >= outreachTotalPages}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {outreachStageFilter ? 'No outreach assignments match this filter' : 'No outreach assignments yet'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {outreachStageFilter
                      ? 'Try changing the filter or clearing it.'
                      : 'Outreach assignments can be created from the CRM page.'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ============================================
            REVIEW QUEUE TAB (superadmin only)
            ============================================ */}
        {activeMainTab === 'review-queue' && isSuperAdmin && (
          <>
            {/* Stats cards */}
            {queueStats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: 'pending', label: 'Pending', border: 'border-purple-200', text: 'text-purple-600' },
                  { key: 'outreach_created', label: 'Outreach Created', border: 'border-green-200', text: 'text-green-600' },
                  { key: 'dismissed', label: 'Dismissed', border: 'border-gray-200', text: 'text-gray-600' },
                  { key: 'total', label: 'Total', border: 'border-purple-300', text: 'text-purple-700' },
                ].map(s => (
                  <div key={s.key} className={`bg-white rounded-lg border ${s.border} p-3 text-center shadow-sm`}>
                    <div className={`text-lg font-bold ${s.text}`}>{queueStats[s.key] || 0}</div>
                    <div className={`text-xs ${s.text}`}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-2">
                {['', 'pending', 'outreach_created', 'dismissed'].map(f => (
                  <button
                    key={f}
                    onClick={() => { setQueueStatusFilter(f); setQueuePage(1); }}
                    className={`px-3 py-1.5 text-xs rounded-full border font-medium ${
                      queueStatusFilter === f
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {f === '' ? 'All' : f === 'outreach_created' ? 'Outreach Created' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                <div className="flex-1" />
                <input
                  type="text"
                  placeholder="Search by name, company, title..."
                  value={queueSearch}
                  onChange={(e) => setQueueSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setQueuePage(1); fetchQueue(1); } }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-56 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <button
                  onClick={() => { setQueuePage(1); fetchQueue(1); }}
                  className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Search
                </button>
                <button
                  onClick={async () => {
                    try {
                      const params = new URLSearchParams();
                      if (queueStatusFilter) params.set('status', queueStatusFilter);
                      if (queueSearch) params.set('search', queueSearch);
                      const res = await axios.get(`${API_URL}/api/exec/leadership-review-queue/export?${params}`, {
                        ...authHeaders(),
                        responseType: 'blob',
                      });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `leadership_review_queue_${new Date().toISOString().slice(0,10)}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error('Export failed:', err);
                      setError('Failed to export CSV.');
                    }
                  }}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </button>
              </div>
            </div>

            {/* Bulk action bar */}
            {selectedPendingIds.size > 0 && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-purple-50 border border-purple-200 rounded-lg">
                <span className="text-sm font-medium text-purple-800">
                  {selectedPendingIds.size} selected
                </span>
                <div className="flex-1" />
                <button
                  onClick={() => {
                    setBulkOutreachForm({ assigned_to_id: '', goal: 'board_recruitment', priority: 'normal', due_date: '', purpose: `Bulk outreach — ${selectedPendingIds.size} high-scoring contact(s) from review queue` });
                    setBulkOutreachModal(true);
                    if (staffList.length === 0) fetchStaffList();
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Create Outreach ({selectedPendingIds.size})
                </button>
                <button
                  onClick={() => { setBulkDismissNotes(''); setBulkDismissModal(true); }}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Dismiss ({selectedPendingIds.size})
                </button>
                <button
                  onClick={() => setSelectedQueueIds(new Set())}
                  className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Queue table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {queueLoading ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-purple-600 border-t-transparent mb-3" />
                  <div className="text-sm">Loading review queue...</div>
                </div>
              ) : queueItems.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-green-50 text-green-800">
                          <th className="px-2 py-3 text-center font-medium sticky left-0 z-20 bg-green-50" style={{ width: '36px', minWidth: '36px' }}>
                            <input
                              type="checkbox"
                              checked={allPendingSelected}
                              onChange={toggleSelectAllPending}
                              className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              title={allPendingSelected ? 'Deselect all' : 'Select all pending'}
                            />
                          </th>
                          <th className="px-3 py-3 text-left font-medium sticky z-20 bg-green-50" style={{ left: '36px', minWidth: '170px' }}>Contact</th>
                          <th className="px-3 py-3 text-left font-medium sticky z-20 bg-green-50" style={{ left: '206px', minWidth: '140px' }}>Company</th>
                          <th className="px-3 py-3 text-left font-medium sticky z-20 bg-green-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: '346px', minWidth: '160px' }}>Title</th>
                          <th className="px-3 py-3 text-left font-medium">Email</th>
                          <th className="px-3 py-3 text-center font-medium">Score</th>
                          <th className="px-3 py-3 text-left font-medium">Band</th>
                          <th className="px-3 py-3 text-center font-medium">Giving</th>
                          <th className="px-3 py-3 text-center font-medium">Wealth</th>
                          <th className="px-3 py-3 text-left font-medium">Added By</th>
                          <th className="px-3 py-3 text-left font-medium">Date Added</th>
                          <th className="px-3 py-3 text-left font-medium">Status</th>
                          <th className="px-3 py-3 text-left font-medium" style={{ minWidth: '200px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {queueItems.map(item => (
                          <tr key={item.id} className={`hover:bg-gray-50 ${item.status === 'pending' ? '' : 'opacity-70'} ${selectedQueueIds.has(item.id) ? 'bg-purple-50' : ''}`}>
                            <td className={`px-2 py-2 text-center sticky left-0 z-10 ${selectedQueueIds.has(item.id) ? 'bg-purple-50' : 'bg-white'}`} style={{ width: '36px', minWidth: '36px' }}>
                              {item.status === 'pending' ? (
                                <input
                                  type="checkbox"
                                  checked={selectedQueueIds.has(item.id)}
                                  onChange={() => toggleSelectQueueItem(item.id)}
                                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                />
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className={`px-3 py-2 sticky z-10 ${selectedQueueIds.has(item.id) ? 'bg-purple-50' : 'bg-white'}`} style={{ left: '36px', minWidth: '170px' }}>
                              <div className="font-medium text-gray-900">
                                {item.contact_linkedin_url ? (
                                  <a href={item.contact_linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    {item.contact_name}
                                  </a>
                                ) : item.contact_name}
                              </div>
                            </td>
                            <td className={`px-3 py-2 text-gray-600 sticky z-10 ${selectedQueueIds.has(item.id) ? 'bg-purple-50' : 'bg-white'}`} style={{ left: '206px', minWidth: '140px' }}>{item.contact_company || '-'}</td>
                            <td className={`px-3 py-2 text-gray-600 text-xs sticky z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${selectedQueueIds.has(item.id) ? 'bg-purple-50' : 'bg-white'}`} style={{ left: '346px', minWidth: '160px' }}>{item.contact_title || '-'}</td>
                            <td className="px-3 py-2">
                              {item.emails && item.emails.length > 0 ? (
                                <div className="flex flex-col gap-0.5">
                                  {item.emails.map((em, i) => (
                                    <a key={i} href={`mailto:${em}`} className="text-xs text-blue-600 hover:underline truncate max-w-[200px]" title={em}>
                                      {em}
                                    </a>
                                  ))}
                                </div>
                              ) : <span className="text-xs text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2 text-center">{scoreBadge(item.leadership_score)}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {item.score_band}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {item.giving_score != null ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                                  item.giving_score >= 70 ? 'bg-green-100 text-green-700' :
                                  item.giving_score >= 40 ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{item.giving_score}</span>
                              ) : <span className="text-xs text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {item.wealth_score != null ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                                  item.wealth_score >= 70 ? 'bg-green-100 text-green-700' :
                                  item.wealth_score >= 40 ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{item.wealth_score}</span>
                              ) : <span className="text-xs text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600">{item.added_by_name || '-'}</td>
                            <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                              {item.added_at ? new Date(item.added_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                item.status === 'pending' ? 'bg-purple-100 text-purple-700' :
                                item.status === 'outreach_created' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {item.status === 'outreach_created' ? 'Outreach Created' :
                                 item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {item.status === 'pending' ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      setCreateOutreachModal(item);
                                      setOutreachForm({ assigned_to_id: '', goal: 'board_recruitment', priority: 'normal', due_date: '', purpose: `High leadership score (${parseFloat(item.leadership_score).toFixed(1)}) - potential outreach candidate` });
                                      if (staffList.length === 0) fetchStaffList();
                                    }}
                                    className="px-2.5 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                                  >
                                    Create Outreach
                                  </button>
                                  <button
                                    onClick={() => { setDismissModal(item); setDismissNotes(''); }}
                                    className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                                  >
                                    Dismiss
                                  </button>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500">
                                  {item.reviewed_by_name && <span>by {item.reviewed_by_name}</span>}
                                  {item.reviewed_at && <span className="ml-1">({new Date(item.reviewed_at).toLocaleDateString()})</span>}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {queueTotalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        Page {queuePage} of {queueTotalPages} ({queueTotal.toLocaleString()} items)
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => fetchQueue(queuePage - 1)}
                          disabled={queuePage <= 1}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => fetchQueue(queuePage + 1)}
                          disabled={queuePage >= queueTotalPages}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <svg className="mx-auto h-12 w-12 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {queueStatusFilter ? 'No items match this filter' : 'No contacts in review queue'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {queueStatusFilter
                      ? 'Try changing the filter or clearing it.'
                      : 'Contacts scoring 6+ on leadership will appear here automatically.'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ============================================
          MODALS
          ============================================ */}

      {/* Not Found Modal (Research) */}
      {notFoundContactId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Mark as Not Found</h3>
            <p className="text-sm text-gray-500 mb-4">This contact could not be found on LinkedIn. Optionally add a note.</p>
            <textarea
              value={notFoundNote}
              onChange={(e) => setNotFoundNote(e.target.value)}
              placeholder="e.g., No LinkedIn presence, common name..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { setNotFoundContactId(null); setNotFoundNote(''); }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handleMarkNotFound(notFoundContactId, notFoundNote)}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Mark Not Found
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Touchpoint Modal (Outreach) */}
      {touchpointModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Log Touchpoint</h3>
            <p className="text-sm text-gray-500 mb-4">Record a contact attempt with <span className="font-medium">{touchpointModal.alumni_name}</span></p>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Channel</label>
                  <select
                    value={touchpointForm.channel}
                    onChange={(e) => setTouchpointForm(f => ({ ...f, channel: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500"
                  >
                    {['phone', 'email', 'linkedin', 'in_person', 'text', 'other'].map(ch => (
                      <option key={ch} value={ch}>{ch === 'in_person' ? 'In Person' : ch.charAt(0).toUpperCase() + ch.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Direction</label>
                  <select
                    value={touchpointForm.direction}
                    onChange={(e) => setTouchpointForm(f => ({ ...f, direction: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="outbound">Outbound</option>
                    <option value="inbound">Inbound</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={touchpointForm.subject}
                  onChange={(e) => setTouchpointForm(f => ({ ...f, subject: e.target.value }))}
                  placeholder="e.g., Intro call, Follow-up email..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Outcome</label>
                <select
                  value={touchpointForm.outcome}
                  onChange={(e) => setTouchpointForm(f => ({ ...f, outcome: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500"
                >
                  <option value="">-- Select --</option>
                  {['sent', 'message_sent', 'no_answer', 'voicemail', 'connected', 'replied', 'bounced', 'opened', 'scheduled'].map(o => (
                    <option key={o} value={o}>{
                      o === 'no_answer' ? 'No Answer' :
                      o === 'message_sent' ? 'Sent Direct Message' :
                      o === 'sent' ? 'Sent Email' :
                      o === 'scheduled' ? 'Scheduled' :
                      o.charAt(0).toUpperCase() + o.slice(1)
                    }</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={touchpointForm.notes}
                  onChange={(e) => setTouchpointForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Details about the interaction..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setTouchpointModal(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleLogTouchpoint}
                disabled={touchpointSaving}
                className="px-4 py-2 text-sm text-white bg-violet-600 hover:bg-violet-700 rounded-lg disabled:bg-gray-300"
              >
                {touchpointSaving ? 'Saving...' : 'Log Touchpoint'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Assignment Modal (Outreach) */}
      {closeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Close Outreach</h3>
            <p className="text-sm text-gray-500 mb-4">Close the outreach assignment for <span className="font-medium">{closeModal.alumni_name}</span></p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Outcome</label>
                <select
                  value={closeForm.outcome}
                  onChange={(e) => setCloseForm(f => ({ ...f, outcome: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500"
                >
                  <option value="successful">Successful</option>
                  <option value="unresponsive">Unresponsive</option>
                  <option value="declined">Declined</option>
                  <option value="deferred">Deferred</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={closeForm.outcome_notes}
                  onChange={(e) => setCloseForm(f => ({ ...f, outcome_notes: e.target.value }))}
                  placeholder="Summary of outcome..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setCloseModal(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseAssignment}
                disabled={closeSaving}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:bg-gray-300"
              >
                {closeSaving ? 'Closing...' : 'Close Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Touchpoints List Modal */}
      {touchpointList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Touchpoints</h3>
                <p className="text-sm text-gray-500">{touchpointList.alumniName}</p>
              </div>
              <button
                onClick={() => setTouchpointList(null)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                &times;
              </button>
            </div>

            {touchpointList.items.length > 0 ? (
              <div className="space-y-3">
                {touchpointList.items.map(tp => (
                  <div key={tp.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 capitalize">
                          {tp.channel === 'in_person' ? 'In Person' : tp.channel}
                        </span>
                        <span className={`text-xs ${tp.direction === 'inbound' ? 'text-green-600' : 'text-gray-500'}`}>
                          {tp.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                        </span>
                        {tp.outcome && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded capitalize">
                            {tp.outcome === 'no_answer' ? 'No Answer' : tp.outcome}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">
                        {tp.contacted_at ? new Date(tp.contacted_at).toLocaleString() : '-'}
                      </span>
                    </div>
                    {tp.subject && <div className="text-sm font-medium text-gray-800">{tp.subject}</div>}
                    {tp.notes && <div className="text-sm text-gray-600 mt-1">{tp.notes}</div>}
                    <div className="text-[11px] text-gray-400 mt-1">by {tp.performed_by_name}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">No touchpoints recorded yet.</div>
            )}
          </div>
        </div>
      )}

      {/* Dismiss Queue Item Modal */}
      {dismissModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Dismiss Queue Item</h3>
            <p className="text-sm text-gray-500 mb-4">
              Dismiss <span className="font-medium">{dismissModal.contact_name}</span> (Score: {parseFloat(dismissModal.leadership_score).toFixed(1)}) from the review queue.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={dismissNotes}
                onChange={(e) => setDismissNotes(e.target.value)}
                placeholder="e.g., Not relevant, already in outreach..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { setDismissModal(null); setDismissNotes(''); }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDismissQueueItem}
                disabled={dismissSaving}
                className="px-4 py-2 text-sm text-white bg-gray-600 hover:bg-gray-700 rounded-lg disabled:bg-gray-300"
              >
                {dismissSaving ? 'Dismissing...' : 'Dismiss'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Outreach from Queue Modal */}
      {createOutreachModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Create Outreach Assignment</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="text-sm font-medium text-green-800">{createOutreachModal.contact_name}</div>
              <div className="text-xs text-green-700 mt-0.5">
                {[createOutreachModal.contact_company, createOutreachModal.contact_title].filter(Boolean).join(' - ') || 'No company/title'}
              </div>
              <div className="text-xs text-green-600 mt-0.5">
                Leadership Score: {scoreBadge(createOutreachModal.leadership_score)} <span className="ml-1 text-green-500">({createOutreachModal.score_band})</span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Assign To *</label>
                <select
                  value={outreachForm.assigned_to_id}
                  onChange={(e) => setOutreachForm(f => ({ ...f, assigned_to_id: e.target.value ? parseInt(e.target.value) : '' }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">-- Select staff member --</option>
                  {staffList.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Goal</label>
                  <select
                    value={outreachForm.goal}
                    onChange={(e) => setOutreachForm(f => ({ ...f, goal: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="board_recruitment">Board Recruitment</option>
                    <option value="membership_upgrade">Membership Upgrade</option>
                    <option value="donation">Donation</option>
                    <option value="event_attendance">Event Attendance</option>
                    <option value="engagement">Engagement</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={outreachForm.priority}
                    onChange={(e) => setOutreachForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={outreachForm.due_date}
                  onChange={(e) => setOutreachForm(f => ({ ...f, due_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Purpose</label>
                <textarea
                  value={outreachForm.purpose}
                  onChange={(e) => setOutreachForm(f => ({ ...f, purpose: e.target.value }))}
                  placeholder="Describe the outreach purpose..."
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setCreateOutreachModal(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOutreachFromQueue}
                disabled={outreachFormSaving || !outreachForm.assigned_to_id}
                className="px-4 py-2 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {outreachFormSaving ? 'Creating...' : 'Create Outreach Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Dismiss Modal */}
      {bulkDismissModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Bulk Dismiss</h3>
            <p className="text-sm text-gray-500 mb-4">
              Dismiss <span className="font-semibold text-purple-700">{selectedPendingIds.size}</span> selected item{selectedPendingIds.size !== 1 ? 's' : ''} from the review queue.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional — applies to all)</label>
              <textarea
                value={bulkDismissNotes}
                onChange={(e) => setBulkDismissNotes(e.target.value)}
                placeholder="e.g., Not relevant for current cycle..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setBulkDismissModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDismiss}
                disabled={bulkDismissSaving}
                className="px-4 py-2 text-sm text-white bg-gray-600 hover:bg-gray-700 rounded-lg disabled:bg-gray-300"
              >
                {bulkDismissSaving ? 'Dismissing...' : `Dismiss ${selectedPendingIds.size} Item${selectedPendingIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Create Outreach Modal */}
      {bulkOutreachModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Bulk Create Outreach</h3>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
              <div className="text-sm font-medium text-purple-800">
                {selectedPendingIds.size} contact{selectedPendingIds.size !== 1 ? 's' : ''} selected
              </div>
              <div className="text-xs text-purple-600 mt-1">
                All selected contacts will be assigned the same outreach parameters below.
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Assign To *</label>
                <select
                  value={bulkOutreachForm.assigned_to_id}
                  onChange={(e) => setBulkOutreachForm(f => ({ ...f, assigned_to_id: e.target.value ? parseInt(e.target.value) : '' }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">-- Select staff member --</option>
                  {staffList.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Goal</label>
                  <select
                    value={bulkOutreachForm.goal}
                    onChange={(e) => setBulkOutreachForm(f => ({ ...f, goal: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="board_recruitment">Board Recruitment</option>
                    <option value="membership_upgrade">Membership Upgrade</option>
                    <option value="donation">Donation</option>
                    <option value="event_attendance">Event Attendance</option>
                    <option value="engagement">Engagement</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={bulkOutreachForm.priority}
                    onChange={(e) => setBulkOutreachForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={bulkOutreachForm.due_date}
                  onChange={(e) => setBulkOutreachForm(f => ({ ...f, due_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Purpose</label>
                <textarea
                  value={bulkOutreachForm.purpose}
                  onChange={(e) => setBulkOutreachForm(f => ({ ...f, purpose: e.target.value }))}
                  placeholder="Describe the outreach purpose..."
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setBulkOutreachModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkCreateOutreach}
                disabled={bulkOutreachSaving || !bulkOutreachForm.assigned_to_id}
                className="px-4 py-2 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {bulkOutreachSaving ? 'Creating...' : `Create ${selectedPendingIds.size} Outreach Assignment${selectedPendingIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAlumniResearchAssignments;
